import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ReplyResponse = {
  id: number;
  content: string;
  parent_id?: number;
  post_id: number;
  created_at: string;
  updated_at: string;
  author: {
    id: number;
    user: {
      id: string;
      full_name: string;
      email: string;
    };
  };
  _count: {
    replies: number;
    likes: number;
  };
  is_liked?: boolean;
  documentTags?: Array<{
    id: number;
    document: {
      id: string;
      original_name: string;
      mime_type: string;
      size: number;
    };
  }>;
  nested_replies?: ReplyResponse[];
};

export type ApiResponse_Get_Replies = ReplyResponse[];

const METHOD = 'GET';
const ENDPOINT = 'posts/{postId}/replies';

export const REPLIES_QUERY_KEY = (params: { postId: number; page?: number; limit?: number }) =>
  getQueryKey(
    METHOD,
    getResolvedEndpoint(ENDPOINT, { postId: params.postId.toString() }),
    { page: params.page || 1, limit: params.limit || 50 }
  );

export const useGetReplies = (
  params: { postId: number; page?: number; limit?: number },
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!params.postId,
    queryKey: REPLIES_QUERY_KEY(params),
    queryFn: async () => {
      try {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());

        const endpoint = getResolvedEndpoint(ENDPOINT, {
          postId: params.postId.toString(),
        });
        const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

        const data = await handleApiCall<ApiResponse_Get_Replies, Record<string, never>>(
          session,
          METHOD,
          url,
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to fetch replies.', { variant: 'error' });
        throw err;
      }
    },
  });
};